import { CommonModule } from "@angular/common";
import { Component, computed, inject, Input, input } from "@angular/core";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";
import { TradeType } from "@gql/offer";
import { FiatService } from "@main-services/currency";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { FiatPrecisionPipeModule } from "@modules/+new/shared/fiat-precision-pipe/fiat-precision-pipe.module";
import { AccountService } from "@modules/account/services/account.service";
import { CheckoutQueryParamName } from "@modules/checkout/entities/checkout.model";
import { CheckoutNavigationService } from "@modules/checkout/services/checkout-navigation.service";
import { PipeModule } from "@modules/core/pipes/pipe.module";
import { TranslationPipeModule } from "@modules/core/pipes/translation-pipe/translation-pipe.module";
import { MarginTradingRouteName } from "@modules/margin-trading/entities/margin-trading.model";
import { MarginTradingEntryHelper } from "@modules/margin-trading/services/marign-trading-entry.helper";
import { PositionLineIndicatorComponent } from "@modules/margin-trading/ui/mt-line-indicator/mt-line-indicator.component";
import { MarginPositionFragment } from "app/api/graphql/bff/generated/fragments.service.graphql-gen";
import { MarginHealthIndicator, UiTheme } from "app/api/graphql/bff/generated/types.graphql-gen";
import { DarkModeService } from "@core-services/dark-mode.service";
import { Observable } from "rxjs";
import { LimitOrdersTrackingFacade } from "@modules/limit-orders/services/orders-rudderstack.facade";
import { OrderFlow } from "@modules/limit-orders/interfaces/limit-order-rudderstack.model";

@Component({
	selector: "mt-summary-card",
	templateUrl: "./mt-summary-card.component.html",
	styleUrls: ["./mt-summary-card.component.scss"],
	imports: [CommonModule, TranslationPipeModule, PipeModule, FiatPrecisionPipeModule, PositionLineIndicatorComponent]
})
export class MtSummaryCardComponent {
	private readonly marginTradingEntryHelper: MarginTradingEntryHelper = inject(MarginTradingEntryHelper);
	private readonly checkoutNavigation: CheckoutNavigationService = inject(CheckoutNavigationService);
	private readonly statsigClientService: StatsigClientService = inject(StatsigClientService);
	private readonly tradableListService: TradableListService = inject(TradableListService);
	private readonly accountService: AccountService = inject(AccountService);
	private readonly fiatService: FiatService = inject(FiatService);
	private readonly trackingService = inject(LimitOrdersTrackingFacade);
	protected readonly darkModeService = inject(DarkModeService);

	public marginPosition = input<MarginPositionFragment>();
	public marginHealthIndicator = MarginHealthIndicator;
	public healthIndicator: MarginHealthIndicator;
	@Input() isHome: boolean = false;

	public canAddPosition$: Observable<boolean> = this.statsigClientService.isFeatureActive(
		FEAT_GATES.MARGIN_TRADING_BUY
	);
	public canClosePosition$: Observable<boolean> = this.statsigClientService.isFeatureActive(
		FEAT_GATES.MARGIN_TRADING_SELL
	);

	defaultFiat = this.fiatService.getItemById(this.accountService.account.getFiatId());
	tradableId: string = "";

	progressBarValue = computed(() => {
		const marginPosition = this.marginPosition();
		this.tradableId = this.tradableListService.getItemByPid(marginPosition.asset.pid)?.getId();
		this.healthIndicator = marginPosition.marginLevel.healthIndicator;
		const range = +marginPosition.marginLevel.maximum.value - +marginPosition.marginLevel.minimum.value;
		if (range === 0) {
			return 0;
		}
		const clampedCurrent = Math.min(
			Math.max(+marginPosition.marginLevel.current?.value, +marginPosition.marginLevel.minimum.value),
			+marginPosition.marginLevel.maximum.value
		);
		return (clampedCurrent - +marginPosition.marginLevel.minimum.value) / range;
	});

	public addToPosition(evt: Event): void {
		evt.stopImmediatePropagation();
		this.trackingService.updateFlow(this.isHome ? OrderFlow.AdpMarginWidget : OrderFlow.HomeMarginWidget);
		const tradable = this.tradableListService.getItemByPid(this.marginPosition().asset.pid);
		this.marginTradingEntryHelper.openMarginTradingFlow(TradeType.Buy, tradable.getId());
	}

	public closePosition(evt: Event): void {
		evt.stopImmediatePropagation();
		this.trackingService.updateFlow(this.isHome ? OrderFlow.AdpMarginWidget : OrderFlow.HomeMarginWidget);
		const tradable = this.tradableListService.getItemByPid(this.marginPosition().asset.pid);
		this.marginTradingEntryHelper.openMarginTradingFlow(TradeType.Sell, tradable.getId());
	}

	protected getLogoUrl(logos: MarginPositionFragment["asset"]["logos"]): string {
		if (this.darkModeService.darkModeEnabled) {
			return (logos.find((l) => l.theme === UiTheme.Dark) ?? logos[0]).url;
		}
		return logos[0].url;
	}

	public handleCardClick(): void {
		this.checkoutNavigation.gotoByNavigation(MarginTradingRouteName.MarginTradingPositionDetail, {
			[CheckoutQueryParamName.AssetId]: this.marginPosition().asset.pid,
			[CheckoutQueryParamName.LeverageDirection]: this.marginPosition().leverageDirection
		});
	}
}
